'use client'

import { useEffect, useMemo, useRef } from 'react'
import { ProductShelf as CduiProductShelf } from '@shipt/cdui-modules/product-shelf'
import { CouponShelf as CduiCouponShelf } from '@shipt/cdui-modules/coupon-shelf'
import { type ResolvedProductShelf } from '@shipt/cdui-modules/product-shelf/schema'
import { type ResolvedCouponShelf } from '@shipt/cdui-modules/coupon-shelf/schema'
import { useQueryCouponsSavings } from '@/features/shop/services/Coupons/queries'
import { trackSponsoredContentLoaded } from '@/features/shared/analytics/promotions'

/**
 * Web counterpart to Nebula's app-owned shelf assemblies. CDUI owns rendering;
 * Segway overlays live coupon state so a successful clip is reflected without
 * waiting for the delivery document to be resolved again.
 */
export function SegwayProductShelf({ formData, resolvedData }: ResolvedProductShelf) {
  const firedBeacons = useRef(new Set<string>())
  const couponSavings = useQueryCouponsSavings().data

  useEffect(() => {
    const analytics = resolvedData.analytics
    const urls = analytics?.aggregateOnLoadBeacons
    if (!urls?.length) return
    const featuredTransactionID = analytics?.featuredTransactionId ?? ''
    // urchin's ads placement for these sponsored products, same as the legacy shelf sends
    const adPlacementId = analytics?.adPlacementId
    for (const beaconUrl of urls) {
      if (firedBeacons.current.has(beaconUrl)) continue
      firedBeacons.current.add(beaconUrl)
      trackSponsoredContentLoaded({ beaconUrl, featuredTransactionID, adPlacementId })
    }
  }, [resolvedData.analytics])
  const products = useMemo(
    () =>
      resolvedData.products.map((product) => {
        const coupon = product.coupon
        if (!coupon) return product

        const liveCoupon = couponSavings?.[coupon.couponId]
        if (!liveCoupon) return product

        const clipped = coupon.clipped || Boolean(liveCoupon.clippedAt)
        const completed = Boolean(liveCoupon.isFulfilled)
        if (clipped === coupon.clipped && completed === coupon.completed) {
          return product
        }

        return {
          ...product,
          coupon: { ...coupon, clipped, completed },
        }
      }),
    [couponSavings, resolvedData.products],
  )

  return <CduiProductShelf formData={formData} resolvedData={{ ...resolvedData, products }} />
}

export function SegwayCouponShelf({ formData, resolvedData }: ResolvedCouponShelf) {
  const couponSavings = useQueryCouponsSavings().data
  const coupons = useMemo(
    () =>
      resolvedData.coupons.map((coupon) => {
        const liveCoupon = couponSavings?.[coupon.coupon_id]
        if (!liveCoupon?.clippedAt || coupon.clipped_at) return coupon

        return { ...coupon, clipped_at: liveCoupon.clippedAt }
      }),
    [couponSavings, resolvedData.coupons],
  )

  return <CduiCouponShelf formData={formData} resolvedData={{ ...resolvedData, coupons }} />
}
